import { IAtClickableProps } from '@/components/atoms/at-clickable/at-clickable';
import { THeaderSupportedElements } from '@/components/atoms/at-header/at-header.types';
import { EIcons } from '@/components/atoms/at-icon/icons';
import { IAtMediaProps } from '@/components/atoms/at-media/at-media.types';
import { IAtTagProps } from '@/components/atoms/at-tag/at-tag';
import { ETagVariants } from '@/components/atoms/at-tag/at-tag.types';
import { IDefaultCsProps, EThemes } from '@/lib/contentstack-sdk/types';
import {
  IAtLinkCMSProps,
  IAtIconCMSProps,
  IImageCMSProps,
  IAtChipCMSProps,
} from '@/lib/contentstack-sdk/utils/parseHelpers';

/**
 * Props for cards pulled from Contentstack.
 * Used for CMS-driven card rendering.
 */
export interface IMlCardsCMSProps extends IDefaultCsProps {
  cta: IAtLinkCMSProps[];
  icon: IAtIconCMSProps[];
  title: string;
  header: string;
  /** Optional heading element (e.g. 'h2', 'h3') */
  header_element?: { select?: THeaderSupportedElements };
  /** Body copy for the card */
  body: string;
  /** Optional model home or plan ID, used in home-plan cards */
  house_plan_id?: string;
  variant: EMlCardsTypes;
  theme: EThemes;
  image: IImageCMSProps;
  /** Content alignment (left, center, right) */
  alignment: TAlignments;
  /** Optional chip with icon and label (e.g. for promos) */
  chip: IAtChipCMSProps[];
  /** Optional ARIA label override */
  aria_label?: string;
}

/**
 * Horizontal alignment options for card content.
 */
export type TAlignments = 'center' | 'left' | 'right';

/**
 * Enum of all supported MlCard layout variants.
 */
export enum EMlCardsTypes {
  /** Icon + copy card (used in icon grids) */
  INFOGRAPHIC = 'infographic',
  /** Image on top, text and CTA below */
  IMAGE = 'image',
  /** Full-bleed image card with overlay text */
  FULL_IMAGE = 'full_image',
  /** Icon-focused layout with left or center alignment */
  ICON = 'icon',
  /** Specialized layout for model home or floor plan cards */
  HOME_PLAN = 'home-plan',
  /** Bold promotional card layout */
  PROMO = 'promo',
  /** CTA-only end card layout */
  SIMPLE_ACTION = 'simple-action',
  /** Compact promo layout (not widely used) */
  PROMO_COMPACT = 'promo-compact',
  /** Used in selection UIs (e.g., stat selector) */
  STAT_CARD = 'stat-card',
}

/**
 * Props for the body of a home plan card.
 */
export interface IHomePlanProps {
  /** ID of the home plan */
  id: number;
  /** Name of the plan (displayed as header) */
  name: string;
  /** Short marketing description */
  description: string;
  /** List of features (beds, baths, sq ft, etc.) */
  features: string[];
  /** List of tags (e.g. Available in 3D, Featured) */
  tags: IAtTagProps[];
  /** ARIA label for the favorite/like button */
  likePlanAriaLabel?: string;
  /** Called when the heart icon is clicked */
  onSaveHomePlanClick?: () => void;
  /** Whether to show the favorite (heart) icon */
  showFavouriteButton?: boolean;
  /** Optional link to detail page */
  href?: string;
  /** Optional heading element for the name */
  headerElement?: THeaderSupportedElements;
}

/**
 * Props for the default body renderer used in all non-home-plan cards.
 */
export interface IDefaultBodyProps {
  /** Optional heading for the card */
  title?: string;
  /** Optional body copy */
  body?: string;
  /** Optional CTA button configuration */
  ctaProps?: IAtClickableProps;
  /** Current card layout variant */
  variant: EMlCardsTypes;
  /** Content alignment */
  alignment: TAlignments;
  /** Semantic heading element (e.g., h4, h3) */
  headerElement: THeaderSupportedElements;
  /** Visual style of the header */
  headerVariant: THeaderSupportedElements;
}

/**
 * Props for an optional badge-style chip at the top of the card.
 */
export interface IAtChipProps {
  label: string;
  icon?: EIcons;
  variant?: ETagVariants;
}

/**
 * Main props for the MlCard component.
 * Handles all logic for rendering icons, images, CTAs, and variants.
 */
export interface IMlCardProps {
  /** Optional CTA button configuration */
  ctaProps?: IAtClickableProps;
  /** Icon to display in icon-based layouts */
  iconName?: EIcons;
  /** Heading text */
  title?: string;
  /** Body copy text */
  body?: string;
  /** Props for home plan card layout */
  homePlanProps?: IHomePlanProps;
  /** Card variant to render */
  variant?: EMlCardsTypes;
  /** Light or dark theme */
  theme?: EThemes;
  /** Image to display in visual card variants */
  imageProps?: IAtMediaProps;
  /** Whether to show the favorite button (only applies to home-plan) */
  showFavouriteButton?: boolean;
  /** Horizontal alignment of content */
  alignment?: TAlignments;
  /** Optional badge-style chip config */
  chipProps?: IAtChipProps;
  /** Additional class names */
  className?: string;
  /** HTML heading element to use */
  headerElement?: THeaderSupportedElements;
  /** Visual header style */
  headerVariant?: THeaderSupportedElements;
  /** Marks card as selected (used in stat-card layout) */
  selected?: boolean;
  /** Optional tab index for keyboard nav */
  tabIndex?: number;
  /** Optional href used in clickable layouts */
  href?: string;
  /** Optional ARIA label override */
  ariaLabel?: string;
  /** Optional click handler for card */
  onClick?: () => void;
  /** Optional focus handler */
  onFocus?: (event: React.FocusEvent<HTMLDivElement>) => void;
}
